import React, { useState } from "react";
import {
  PartWithInventory,
  usePartPlacementType,
} from "../../../utils/hooks/inventory/boardInventory";
import {
  EditableCellContainer,
  ModalContainer,
  PlacementTypeOptions,
  Title,
  VerticalList,
} from "./BoardInventoryStyles";
import { useCustomAlert, useCustomConfirm } from "../../../utils/hooks/alerts";
import Button from "../../common/Button";
import { EditButton } from "../../ReviewBoardFlow/BillOfMaterials/ElementsTable/ElementsTableStyles";
import Icon from "../../common/Icon";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import { Board } from "../../../reducers/uploadedBoard";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import LoadingOverlay from "../../LoadingOverlay";
import { PlacementType } from "../../../reducers/models/parts";
import { InputLabel, Pill } from "../../../constants/Styles";
import RadioButton from "../../common/RadioButton";

export default function PlacementTypeCell({
  part,
  updating = false,
}: {
  part: PartWithInventory;
  // The shipment is being reprocessed, so what it says about the part may
  // be about to change
  updating?: boolean;
}) {
  const board = useBoardFromContext().board as Board;
  const customConfirm = useCustomConfirm();
  const [placementType, setPlacementType] = useState<string>(
    part.placement_type || "",
  );
  const [updatePlacementModalOpen, setUpdatePlacementModalOpen] =
    useState(false);
  const customAlert = useCustomAlert();
  const errorHandler = (detail: string) => {
    customAlert(detail);
    // Nothing changed, so put the selection back where it was
    setPlacementType(part.placement_type || "");
  };
  const { updatePartPlacementType, loading } = usePartPlacementType(
    board.id,
    undefined,
    errorHandler,
  );

  const updatePlacement = () => {
    if (!placementType) {
      return;
    }
    customConfirm(
      "Would you like to update the placement type for all future projects or this project only?",
      () => {
        updatePartPlacementType(part.part.id, placementType, true);
        setUpdatePlacementModalOpen(false);
      },
      () => {
        updatePartPlacementType(part.part.id, placementType, false);
        setUpdatePlacementModalOpen(false);
      },
      {
        confirmText: "Update part globally",
        cancelText: "Update for this project only",
      },
    );
  };

  return (
    <EditableCellContainer>
      <LoadingOverlay loading={loading} text={"Updating placement type..."} />
      <>
        <VerticalList>
          <InputLabel>Placement type: </InputLabel>
          {updating ? (
            <Pill width={"5rem"} />
          ) : (
            <div>{part.placement_type || "Unknown"}</div>
          )}
        </VerticalList>
        <EditButton
          onClick={
            loading || updating
              ? undefined
              : () => setUpdatePlacementModalOpen(true)
          }
          disabled={loading || updating}
        >
          <Icon src="/icons/pencil.svg" alt="edit" />
        </EditButton>
      </>
      <Modal
        isOpen={!loading && updatePlacementModalOpen}
        onRequestClose={() => setUpdatePlacementModalOpen(false)}
      >
        <ModalContentWithCloseButton
          closeModal={() => setUpdatePlacementModalOpen(false)}
        >
          <ModalContainer>
            <Title>{`Update placement type for ${part.part.name}`}</Title>
            <PlacementTypeOptions>
              {Object.values(PlacementType).map((placementTypeOption) => {
                return (
                  <RadioButton
                    key={placementTypeOption}
                    onChange={() => setPlacementType(placementTypeOption)}
                    checked={placementTypeOption == placementType}
                    label={placementTypeOption}
                    name={placementTypeOption}
                  />
                );
              })}
            </PlacementTypeOptions>
            <Button label={"Submit"} onClick={updatePlacement} />
            <Button
              label={"Cancel"}
              onClick={() => setUpdatePlacementModalOpen(false)}
            />
          </ModalContainer>
        </ModalContentWithCloseButton>
      </Modal>
    </EditableCellContainer>
  );
}
